// Client-side game state: applies server snapshots, runs movement prediction with server
// reconciliation, and interpolates everything else. No drawing happens in here.

import { CLASSES, SKILLS } from '../../shared/classes';
import { findPath, moveWithCollision, tileAt } from '../../shared/map';
import { generateZoneMap } from '../../shared/mapgen';
import { MONSTERS } from '../../shared/monsters';
import { ANIM_DEAD, C2S, ELEMENT_COLORS, EntityMeta, Fx, ItemMeta, ME_DOWNED, ME_FORCED, ME_STUNNED, MeRec, ObjMeta, PartyEntry, S2C, UnitMeta } from '../../shared/protocol';
import { RealmDef, ZoneInfo, parseZone } from '../../shared/realms';
import { GearLook, NO_GEAR, decodeGear, dressedLook } from '../../shared/look';
import { xpToNext } from '../../shared/stats';
import { ClassId, Derived, HeroSave, PLAYER_RADIUS, SkillDef, T_FLOOR, T_WALL, TICK_DT, TICK_MS, ZoneMap, rv } from '../../shared/types';
import { audio } from '../audio';
import { input } from '../input';

export interface Sample {
  k: number;
  x: number;
  y: number;
}

export interface CUnit {
  id: number;
  kind: 'player' | 'monster' | 'summon';
  def: string;
  name: string;
  level: number;
  tier: number;
  team: number;
  extra: string;
  /** Worn equipment (players only), decoded from extra. */
  gear: GearLook;
  samples: Sample[];
  x: number;
  y: number;
  hp: number;
  dirAnim: number;
  status: number;
  flash: number;
  walk: number;
  lastX: number;
  lastY: number;
  /** Animation clock for swings. */
  animT: number;
  lastAnim: number;
  hopT: number;
  hopDur: number;
  deadAt: number;
  visible: boolean;
}

export interface CItem {
  id: number;
  x: number;
  y: number;
  rarity: number;
  icon: string;
  name: string;
  born: number;
}

export interface CObject {
  id: number;
  type: string;
  x: number;
  y: number;
  state: number;
  sub: string;
  to: string;
}

export interface CProj {
  id: number;
  vis: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  total: number;
  r: number;
  lob: boolean;
  sx: number;
  sy: number;
  tx: number;
  ty: number;
}

export interface CGround {
  id: number;
  vis: string;
  x: number;
  y: number;
  r: number;
  until: number;
  born: number;
}

export interface CEffect {
  vis: string;
  x: number;
  y: number;
  r: number;
  ang: number;
  t: number;
  dur: number;
}

export interface CLine {
  vis: string;
  pts: number[];
  t: number;
  dur: number;
}

export interface CTelegraph {
  x: number;
  y: number;
  r: number;
  t: number;
  dur: number;
  el: number;
}

export interface Float {
  x: number;
  y: number;
  text: string;
  color: string;
  t: number;
  dur: number;
  big: boolean;
  vx: number;
}

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  grav: number;
  life: number;
  total: number;
  color: string;
  size: number;
}

export interface Banner {
  text: string;
  color: string;
  t: number;
}

export interface ChatLine {
  from: string;
  text: string;
  color: string;
  at: number;
}

export type MoveGoal =
  | { kind: 'point'; x: number; y: number }
  | { kind: 'unit'; id: number; skill: string }
  | { kind: 'act'; id: number; x: number; y: number; range: number };

const VIS_SFX: Record<string, string> = {
  explosion: 'explode', explosionGreen: 'explode', meteor: 'explode', orbital: 'explode', shockwave: 'slam', frostnova: 'ice', sparkBurst: 'zap', voidBurst: 'warp',
};

export class GameState {
  map: ZoneMap | null = null;
  zone: ZoneInfo | null = null;
  realm: RealmDef | null = null;
  roomSeed = 0;
  roomCode = '';
  roomName = '';
  difficulty = 0;
  myUid = 0;
  myPid = 0;
  /** Zone id generation counter, bumped so the renderer rebuilds its caches. */
  zoneGen = 0;

  hero: HeroSave | null = null;
  derived: Derived | null = null;
  me: MeRec = [1, 1, 1, 1, 5, 0, 0, 0, 0, 0];

  units = new Map<number, CUnit>();
  items = new Map<number, CItem>();
  objects = new Map<number, CObject>();
  projs = new Map<number, CProj>();
  grounds = new Map<number, CGround>();
  effects: CEffect[] = [];
  lines: CLine[] = [];
  telegraphs: CTelegraph[] = [];
  floats: Float[] = [];
  particles: Particle[] = [];
  banners: Banner[] = [];
  chat: ChatLine[] = [];
  party: PartyEntry[] = [];
  shake = 0;
  ping = 0;
  /** When the room closes for an update, on the performance.now() clock. 0 if it isn't closing. */
  private closingAt = 0;

  /** Predicted position of the local hero. */
  px = 0;
  py = 0;
  prevPx = 0;
  prevPy = 0;
  offX = 0;
  offY = 0;
  private seq = 0;
  private history: { s: number; x: number; y: number }[] = [];
  private stepAcc = 0;
  private wasMoving = false;
  private stuckSteps = 0;
  private lastInputAt = 0;

  latestTick = 0;
  private lastSnapAt = 0;
  renderTick = 0;

  now = 0;

  cooldowns = new Map<string, { until: number; dur: number }>();
  private nextCastAt = 0;
  private complainUntil = 0;
  castLockUntil = 0;
  goal: MoveGoal | null = null;
  private path: { x: number; y: number }[] | null = null;
  private pathAt = 0;

  /** Explored tiles. */
  explored: Uint8Array = new Uint8Array(0);
  visible: Uint8Array = new Uint8Array(0);
  private visTile = -1;
  mapDirty = true;
  exploredNew: number[] = [];
  /** Whether the HUD should lock movement (chat etc). */
  inputLocked = false;
  connected = false;
  statusText = '';
  /** Prediction health, shown on the debug overlay. */
  netStats = { samples: 0, corrections: 0, maxErr: 0 };

  constructor(public send: (m: C2S) => void) {}

  // ─── Network ────────────────────────────────────────────────────────────

  onMessage(m: S2C): void {
    switch (m.t) {
      case 'welcome':
        this.myPid = m.pid;
        this.myUid = m.uid;
        this.roomSeed = m.room.seed;
        this.roomCode = m.room.code;
        this.roomName = m.room.name;
        this.difficulty = m.room.diff;
        this.connected = true;
        // If the room is still closing, it says so straight after this.
        this.closingAt = 0;
        break;
      case 'closing':
        this.closingAt = performance.now() + m.left * 1000;
        break;
      case 'zone':
        this.enterZone(m.id, m.x, m.y, m.hash, m.uid);
        break;
      case 'hero': {
        const firstHero = !this.hero;
        const prevLevel = this.hero?.level ?? 0;
        this.hero = m.hero;
        this.derived = m.d;
        if (!firstHero && m.hero.level > prevLevel) audio.play('levelup');
        break;
      }
      case 's':
        this.applySnapshot(m);
        break;
      case 'party':
        this.party = m.l;
        break;
      case 'chat':
        this.pushChat(m.f, m.m, m.c);
        break;
      case 'pong':
        this.ping = Math.round(performance.now() - m.c);
        break;
      case 'kick':
        this.pushChat('', m.m, '#f87858');
        break;
      default:
        break;
    }
  }

  /** Seconds until the room closes for an update, or null if it isn't closing. */
  closingLeft(): number | null {
    return this.closingAt ? (this.closingAt - performance.now()) / 1000 : null;
  }

  pushChat(from: string, text: string, color: string): void {
    this.chat.push({ from, text, color, at: performance.now() });
    if (this.chat.length > 80) this.chat.shift();
  }

  private enterZone(id: string, x: number, y: number, hash: number, uid: number): void {
    const map = generateZoneMap(this.roomSeed, id);
    if (map.hash !== hash) {
      console.error(`map desync: client ${map.hash} server ${hash}`);
      this.pushChat('', 'Warning: map desync detected, please reload.', '#f83800');
    }
    this.map = map;
    this.zone = parseZone(id);
    this.realm = this.zone.realm;
    this.myUid = uid;
    this.units.clear();
    this.items.clear();
    this.objects.clear();
    this.projs.clear();
    this.grounds.clear();
    this.effects = [];
    this.lines = [];
    this.telegraphs = [];
    this.floats = [];
    this.particles = [];
    this.px = this.prevPx = x;
    this.py = this.prevPy = y;
    this.offX = this.offY = 0;
    this.history = [];
    this.goal = null;
    this.path = null;
    this.zoneGen++;
    this.explored = new Uint8Array(map.w * map.h);
    this.visible = new Uint8Array(map.w * map.h);
    this.visTile = -1;
    this.mapDirty = true;
    audio.setMusic(this.realm.music);
    audio.play('zone');
    this.banners.push({ text: this.zone.name, color: '#f8d878', t: 0 });
  }

  private applySnapshot(m: Extract<S2C, { t: 's' }>): void {
    this.latestTick = m.k;
    this.lastSnapAt = performance.now();
    const prevHp = this.me[0];
    this.me = m.m;

    for (const meta of m.n) this.applyMeta(meta);

    for (const rec of m.e) {
      const u = this.units.get(rec[0]);
      if (!u) continue;
      const x = rec[1] / 32;
      const y = rec[2] / 32;
      const s = u.samples;
      if (s.length && s[s.length - 1].k < m.k - 1) {
        // Was stationary: hold position until the tick before this movement started.
        const last = s[s.length - 1];
        s.push({ k: m.k - 1, x: last.x, y: last.y });
      }
      s.push({ k: m.k, x, y });
      if (s.length > 8) s.shift();
      const wasDead = u.hp <= 0;
      u.hp = rec[3];
      const newAnim = rec[4] >> 2;
      if (newAnim !== u.lastAnim) {
        u.lastAnim = newAnim;
        u.animT = 0;
      }
      u.dirAnim = rec[4];
      u.status = rec[5];
      if (!wasDead && newAnim === ANIM_DEAD) u.deadAt = performance.now();
      if (wasDead && newAnim !== ANIM_DEAD) u.deadAt = 0;
    }

    for (const id of m.g) {
      this.units.delete(id);
      this.items.delete(id);
      this.objects.delete(id);
    }

    if (m.o) {
      for (const [id, state] of m.o) {
        const o = this.objects.get(id);
        if (o) o.state = state;
      }
    }

    this.reconcile(m.a, m.x, m.y);

    if (m.fx) for (const f of m.fx) this.applyFx(f);

    if (m.m[0] < prevHp - Math.max(1, m.m[2] * 0.02)) {
      audio.play('hurt');
      const u = this.units.get(this.myUid);
      if (u) u.flash = 1;
      this.shake = Math.max(this.shake, 1.5);
    }
  }

  private applyMeta(meta: EntityMeta): void {
    const kind = meta[1];
    if (kind === 0) {
      const mm = meta as UnitMeta;
      const old = this.units.get(mm[0]);
      if (old) {
        old.name = mm[4];
        old.level = mm[5];
        old.team = mm[7];
        old.extra = mm[8];
        old.gear = mm[2] === 'player' ? decodeGear(mm[8]) : NO_GEAR;
        return;
      }
      this.units.set(mm[0], {
        id: mm[0], kind: mm[2], def: mm[3], name: mm[4], level: mm[5], tier: mm[6], team: mm[7], extra: mm[8], gear: mm[2] === 'player' ? decodeGear(mm[8]) : NO_GEAR, samples: [], x: 0, y: 0, hp: 100,
        dirAnim: 0, status: 0, flash: 0, walk: 0, lastX: 0, lastY: 0, animT: 0, lastAnim: 0, hopT: 0, hopDur: 0, deadAt: 0, visible: true,
      });
    } else if (kind === 1) {
      const im = meta as ItemMeta;
      this.items.set(im[0], { id: im[0], x: im[2], y: im[3], rarity: im[4], icon: im[5], name: im[6], born: performance.now() });
    } else {
      const om = meta as ObjMeta;
      this.objects.set(om[0], { id: om[0], type: om[2], x: om[3], y: om[4], state: om[5], sub: om[6], to: om[7] });
    }
  }

  private reconcile(ack: number, sx: number, sy: number): void {
    const forced = (this.me[9] & ME_FORCED) !== 0;
    let refX: number | null = null;
    let refY = 0;
    while (this.history.length && this.history[0].s < ack) this.history.shift();
    const h = this.history[0];
    if (h && h.s === ack) {
      refX = h.x;
      refY = h.y;
    } else if (!this.history.length || performance.now() - this.lastInputAt > 300) {
      // Nothing in flight: the server's position is simply the truth.
      refX = this.px;
      refY = this.py;
    }
    if (refX === null) return;
    const ex = sx - refX;
    const ey = sy - refY;
    const err = Math.hypot(ex, ey);
    this.netStats.samples++;
    this.netStats.maxErr = Math.max(this.netStats.maxErr, err);
    if (err < 0.03) return;
    this.netStats.corrections++;
    if (err > 4 || forced) {
      // Teleports and server-driven dashes. Snap with a little visual smoothing.
      this.px += ex;
      this.py += ey;
      this.prevPx = this.px;
      this.prevPy = this.py;
      this.offX -= err > 4 ? 0 : ex;
      this.offY -= err > 4 ? 0 : ey;
      for (const e of this.history) {
        e.x += ex;
        e.y += ey;
      }
      return;
    }
    this.px += ex;
    this.py += ey;
    this.prevPx += ex;
    this.prevPy += ey;
    for (const e of this.history) {
      e.x += ex;
      e.y += ey;
    }
    this.offX -= ex;
    this.offY -= ey;
  }

  // ─── Effects ────────────────────────────────────────────────────────────

  private applyFx(f: Fx): void {
    const code = f[0] as string;
    const x = f[1] as number;
    const y = f[2] as number;
    switch (code) {
      case 'pr': {
        const id = f[3] as number;
        const life = f[7] as number;
        this.projs.set(id, {
          id, vis: f[4] as string, x, y, vx: f[5] as number, vy: f[6] as number, life, total: life, r: f[8] as number, lob: f[9] === 1, sx: x, sy: y, tx: f[10] as number, ty: f[11] as number,
        });
        break;
      }
      case 'pu': {
        const p = this.projs.get(f[3] as number);
        if (p) {
          p.x = x;
          p.y = y;
          p.vx = f[4] as number;
          p.vy = f[5] as number;
        }
        break;
      }
      case 'pe': {
        const p = this.projs.get(f[3] as number);
        if (p) {
          this.burst(x, y, 6, projColor(p.vis), 40, 0.25);
          this.projs.delete(p.id);
        }
        break;
      }
      case 'd': {
        const amt = f[3] as number;
        const el = f[4] as number;
        const crit = f[5] === 1;
        const tid = f[6] as number;
        const isDot = f[7] === 1;
        const u = this.units.get(tid);
        if (u) u.flash = isDot ? Math.max(u.flash, 0.3) : 1;
        const isMe = tid === this.myUid;
        if (!isDot || amt > 3) {
          this.floats.push({
            x: x + (Math.random() - 0.5) * 0.5, y: y - 0.8, text: String(amt), color: isMe ? '#f83800' : crit ? '#f8d878' : ELEMENT_COLORS[el] ?? '#fcfcfc',
            t: 0, dur: crit ? 0.9 : 0.65, big: crit, vx: (Math.random() - 0.5) * 12,
          });
        }
        if (!isDot) {
          audio.play('hit', 0.7);
          this.burst(x, y - 0.3, crit ? 8 : 3, u?.kind === 'monster' ? bloodColor(u.def) : '#d82800', 55, 0.3);
        }
        if (crit) this.shake = Math.max(this.shake, 1.2);
        break;
      }
      case 'h':
        this.floats.push({ x: x + (Math.random() - 0.5) * 0.4, y: y - 0.8, text: `+${f[3]}`, color: '#58f898', t: 0, dur: 0.8, big: false, vx: 0 });
        break;
      case 'v': {
        const vis = f[3] as string;
        const r = f[4] as number;
        const ang = (f[5] as number) ?? 0;
        if (vis !== 'none') this.addVisual(vis, x, y, r, ang);
        break;
      }
      case 't':
        this.telegraphs.push({ x, y, r: f[3] as number, t: 0, dur: f[4] as number, el: f[5] as number });
        break;
      case 'g': {
        const id = f[3] as number;
        const dur = f[6] as number;
        this.grounds.set(id, { id, vis: f[4] as string, x, y, r: f[5] as number, until: performance.now() + dur * 1000, born: performance.now() });
        break;
      }
      case 'ge':
        this.grounds.delete(f[3] as number);
        break;
      case 'l': {
        const vis = f[3] as string;
        this.lines.push({ vis, pts: f[4] as number[], t: 0, dur: vis === 'lightning' ? 0.22 : 0.3 });
        break;
      }
      case 'c': {
        const uid = f[3] as number;
        const sfx = f[4] as string;
        const vol = uid === this.myUid ? 1 : 0.55;
        audio.play(sfx, vol);
        break;
      }
      case 'lv': {
        const uid = f[3] as number;
        this.addVisual('levelup', x, y, 2, 0);
        if (uid !== this.myUid) audio.play('heal', 0.5);
        break;
      }
      case 'dth': {
        const u = this.units.get(f[3] as number);
        this.burst(x, y - 0.3, u?.tier === 3 ? 60 : u?.tier ? 22 : 12, u?.kind === 'monster' ? bloodColor(u.def) : '#d82800', 70, 0.5);
        audio.play('die', 0.7);
        if (u?.tier === 3) this.shake = 6;
        if (u && u.id === this.myUid) audio.play('youdied');
        break;
      }
      case 'cd':
        this.cooldowns.set(f[3] as string, { until: performance.now() + (f[4] as number) * 1000, dur: f[4] as number });
        break;
      case 'bnr':
        this.banners.push({ text: f[3] as string, color: f[4] as string, t: 0 });
        break;
      case 'hop': {
        const u = this.units.get(f[3] as number);
        if (u) {
          u.hopT = 0;
          u.hopDur = f[4] as number;
        }
        break;
      }
      case 'ms':
        this.floats.push({ x, y: y - 1.2, text: f[3] as string, color: f[4] as string, t: 0, dur: 1.4, big: false, vx: 0 });
        break;
      case 's':
        audio.play(f[3] as string);
        break;
      default:
        break;
    }
  }

  private addVisual(vis: string, x: number, y: number, r: number, ang: number): void {
    const dur =
      vis.startsWith('slash') || vis === 'clawHit' || vis === 'coldHit' || vis === 'poisonHit' ? 0.16
        : vis === 'whirl' ? 0.24
          : vis === 'orbital' ? 0.55
            : vis === 'levelup' ? 1.1
              : 0.42;
    this.effects.push({ vis, x, y, r, ang, t: 0, dur });
    const sfx = VIS_SFX[vis];
    if (sfx) audio.play(sfx);
    if (vis === 'explosion' || vis === 'explosionGreen' || vis === 'meteor' || vis === 'orbital') {
      this.shake = Math.max(this.shake, Math.min(5, 1.5 + r));
      const col = vis === 'explosionGreen' ? '#58d854' : vis === 'orbital' ? '#a4e4fc' : '#f87800';
      this.burst(x, y, Math.min(46, 14 + r * 8), col, 90 + r * 16, 0.55);
      this.burst(x, y, 10, '#585858', 34, 0.9);
    } else if (vis === 'frostnova') {
      this.burst(x, y, 26, '#a4e4fc', 40 + r * 18, 0.5);
    } else if (vis === 'holynova') {
      this.burst(x, y, 26, '#f8d878', 40 + r * 18, 0.5);
    } else if (vis === 'voidBurst') {
      this.burst(x, y, 26, '#d800cc', 60 + r * 12, 0.5);
    } else if (vis === 'sparkBurst') {
      this.burst(x, y, 20, '#f8d878', 80 + r * 16, 0.35);
    } else if (vis === 'shockwave' || vis === 'shockwaveRed') {
      this.burst(x, y, 14, '#8c7c60', 60 + r * 16, 0.4);
      this.shake = Math.max(this.shake, 2);
    } else if (vis === 'warpPuff') {
      this.burst(x, y - 0.3, 14, '#b890fc', 40, 0.4);
    } else if (vis === 'bonePuff') {
      this.burst(x, y - 0.3, 14, '#d8d8d8', 40, 0.4);
    } else if (vis === 'healPuff') {
      this.burstUp(x, y, 12, '#58f898');
    } else if (vis === 'manaPuff') {
      this.burstUp(x, y, 12, '#3cbcfc');
    } else if (vis === 'buffRed') {
      this.burstUp(x, y, 12, '#f83800');
    } else if (vis === 'buffBlue') {
      this.burstUp(x, y, 12, '#3cbcfc');
    } else if (vis === 'buffGreen') {
      this.burstUp(x, y, 12, '#58f898');
    } else if (vis === 'buffGold') {
      this.burstUp(x, y, 12, '#f8d878');
    } else if (vis === 'buffGrey') {
      this.burstUp(x, y, 12, '#bcbcbc');
    } else if (vis === 'levelup') {
      for (let i = 0; i < 3; i++) this.burstUp(x, y, 16, ['#f8d878', '#fcfcfc', '#f8b800'][i]);
    } else if (vis === 'curse') {
      this.burstUp(x, y, 14, '#b890fc');
    }
  }

  burst(x: number, y: number, n: number, color: string, speed: number, life: number): void {
    for (let i = 0; i < n && this.particles.length < 700; i++) {
      const a = Math.random() * Math.PI * 2;
      const s = speed * (0.3 + Math.random() * 0.7);
      this.particles.push({
        x: x * 16, y: y * 16, vx: Math.cos(a) * s, vy: Math.sin(a) * s - 20, grav: 90, life: life * (0.5 + Math.random() * 0.5), total: life, color, size: Math.random() < 0.3 ? 2 : 1,
      });
    }
  }

  burstUp(x: number, y: number, n: number, color: string): void {
    for (let i = 0; i < n && this.particles.length < 700; i++) {
      this.particles.push({
        x: x * 16 + (Math.random() - 0.5) * 14, y: y * 16 + (Math.random() - 0.5) * 4, vx: (Math.random() - 0.5) * 6, vy: -18 - Math.random() * 28, grav: 0,
        life: 0.5 + Math.random() * 0.5, total: 1, color, size: 1,
      });
    }
  }

  // ─── Update ─────────────────────────────────────────────────────────────

  update(dt: number): void {
    this.now = performance.now();
    if (!this.map) return;
    this.stepAcc += dt;
    let guard = 0;
    while (this.stepAcc >= TICK_DT && guard++ < 5) {
      this.stepAcc -= TICK_DT;
      this.fixedStep();
    }
    if (this.stepAcc > 0.25) this.stepAcc = 0;

    // Interpolation clock
    const sinceSnap = this.now - this.lastSnapAt;
    this.renderTick = this.latestTick - 2 + Math.min(1.5, sinceSnap / TICK_MS);

    const alpha = this.stepAcc / TICK_DT;
    const decay = Math.exp(-dt * 10);
    this.offX *= decay;
    this.offY *= decay;
    if (Math.abs(this.offX) < 0.001) this.offX = 0;
    if (Math.abs(this.offY) < 0.001) this.offY = 0;

    for (const u of this.units.values()) {
      if (u.id === this.myUid) {
        u.x = this.prevPx + (this.px - this.prevPx) * alpha + this.offX;
        u.y = this.prevPy + (this.py - this.prevPy) * alpha + this.offY;
      } else {
        this.interp(u);
      }
      const moved = Math.hypot(u.x - u.lastX, u.y - u.lastY);
      u.walk += moved * 3.2;
      u.lastX = u.x;
      u.lastY = u.y;
      u.flash = Math.max(0, u.flash - dt * 6);
      u.animT += dt;
      if (u.hopDur > 0) {
        u.hopT += dt;
        if (u.hopT >= u.hopDur) u.hopDur = 0;
      }
    }

    for (const p of this.projs.values()) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.life -= dt;
      if (p.life < -0.35) this.projs.delete(p.id);
    }
    for (const g of this.grounds.values()) if (g.until < this.now) this.grounds.delete(g.id);

    this.effects = this.effects.filter((e) => {
      e.t += dt;
      return e.t < e.dur;
    });
    this.lines = this.lines.filter((l) => {
      l.t += dt;
      return l.t < l.dur;
    });
    this.telegraphs = this.telegraphs.filter((tg) => {
      tg.t += dt;
      return tg.t < tg.dur;
    });
    this.floats = this.floats.filter((f) => {
      f.t += dt;
      return f.t < f.dur;
    });
    this.banners = this.banners.filter((b) => {
      b.t += dt;
      return b.t < 3.5;
    });
    let w = 0;
    for (const p of this.particles) {
      p.life -= dt;
      if (p.life <= 0) continue;
      p.vy += p.grav * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      this.particles[w++] = p;
    }
    this.particles.length = w;
    this.shake = Math.max(0, this.shake - dt * 8);

    this.updateVisibility();
  }

  private interp(u: CUnit): void {
    const s = u.samples;
    if (!s.length) return;
    const rt = this.renderTick;
    if (rt >= s[s.length - 1].k || s.length === 1) {
      const l = s[s.length - 1];
      u.x = l.x;
      u.y = l.y;
      return;
    }
    for (let i = s.length - 2; i >= 0; i--) {
      if (s[i].k <= rt) {
        const a = s[i];
        const b = s[i + 1];
        const f = (rt - a.k) / Math.max(1, b.k - a.k);
        u.x = a.x + (b.x - a.x) * f;
        u.y = a.y + (b.y - a.y) * f;
        return;
      }
    }
    u.x = s[0].x;
    u.y = s[0].y;
  }

  /** One movement step: identical in duration to a server tick. */
  private fixedStep(): void {
    this.prevPx = this.px;
    this.prevPy = this.py;
    const map = this.map;
    if (!map) return;
    const dir = this.computeMoveDir();
    const stunned = (this.me[9] & (ME_DOWNED | ME_STUNNED | ME_FORCED)) !== 0;
    const locked = performance.now() < this.castLockUntil;
    if (dir && !stunned && !locked && !this.inputLocked) {
      const a = Math.round((Math.atan2(dir.y, dir.x) / (Math.PI * 2)) * 256) & 255;
      const ang = (a / 256) * Math.PI * 2;
      const speed = this.me[4];
      const dx = Math.cos(ang) * speed * TICK_DT;
      const dy = Math.sin(ang) * speed * TICK_DT;
      const res = moveWithCollision(map, this.px, this.py, PLAYER_RADIUS, dx, dy);
      this.px = res.x;
      this.py = res.y;
      this.seq++;
      this.history.push({ s: this.seq, x: this.px, y: this.py });
      if (this.history.length > 80) this.history.shift();
      this.send({ t: 'i', s: this.seq, a });
      this.lastInputAt = performance.now();
      this.wasMoving = true;
      // Walking into a wall with nowhere else to go: give up rather than push forever.
      if (this.goal && Math.hypot(this.px - this.prevPx, this.py - this.prevPy) < speed * TICK_DT * 0.1) {
        if (++this.stuckSteps >= 8) this.stopMoving();
      } else {
        this.stuckSteps = 0;
      }
    } else if (this.wasMoving) {
      this.seq++;
      this.history.push({ s: this.seq, x: this.px, y: this.py });
      this.send({ t: 'i', s: this.seq, a: -1 });
      this.lastInputAt = performance.now();
      this.wasMoving = false;
    }
  }

  private computeMoveDir(): { x: number; y: number } | null {
    let kx = 0;
    let ky = 0;
    if (!this.inputLocked && !input.textMode) {
      if (input.down.has('KeyW') || input.down.has('ArrowUp')) ky -= 1;
      if (input.down.has('KeyS') || input.down.has('ArrowDown')) ky += 1;
      if (input.down.has('KeyA') || input.down.has('ArrowLeft')) kx -= 1;
      if (input.down.has('KeyD') || input.down.has('ArrowRight')) kx += 1;
    }
    if (kx || ky) {
      this.goal = null;
      this.path = null;
      return { x: kx, y: ky };
    }
    const goal = this.goal;
    if (!goal) return null;
    let gx = 0;
    let gy = 0;
    let stop = 0.15;
    if (goal.kind === 'point') {
      gx = goal.x;
      gy = goal.y;
    } else if (goal.kind === 'act') {
      gx = goal.x;
      gy = goal.y;
      stop = goal.range;
    } else {
      const t = this.units.get(goal.id);
      if (!t || t.hp <= 0) {
        this.goal = null;
        this.path = null;
        return null;
      }
      gx = t.x;
      gy = t.y;
      const reach = SKILLS[goal.skill]?.reach ?? 1.8;
      stop = reach - 0.3;
    }
    const dx = gx - this.px;
    const dy = gy - this.py;
    const d = Math.hypot(dx, dy);
    if (d <= stop) {
      if (goal.kind === 'point') this.goal = null;
      this.path = null;
      return null;
    }
    const map = this.map!;
    // Straight line when nothing's in the way, else A*.
    if (this.clearWalk(this.px, this.py, gx, gy)) {
      this.path = null;
      return { x: dx, y: dy };
    }
    const now = performance.now();
    if (!this.path || now - this.pathAt > 300) {
      this.pathAt = now;
      this.path = findPath(map, this.px, this.py, gx, gy);
      // No route (or too far to search): head straight there until blocked, like a click on
      // the far side of a wall should. fixedStep cancels the goal once we stop making progress.
      if (!this.path) return { x: dx, y: dy };
    }
    while (this.path.length) {
      const wp = this.path[0];
      if (Math.hypot(wp.x - this.px, wp.y - this.py) < 0.35) this.path.shift();
      else break;
    }
    // Skip ahead to the furthest waypoint we can see, which smooths out grid paths.
    for (let i = Math.min(this.path.length - 1, 4); i > 0; i--) {
      if (this.clearWalk(this.px, this.py, this.path[i].x, this.path[i].y)) {
        this.path.splice(0, i);
        break;
      }
    }
    const wp = this.path[0];
    if (!wp) {
      if (goal.kind === 'point') this.goal = null;
      return null;
    }
    return { x: wp.x - this.px, y: wp.y - this.py };
  }

  private clearWalk(x0: number, y0: number, x1: number, y1: number): boolean {
    const map = this.map!;
    const dist = Math.hypot(x1 - x0, y1 - y0);
    const steps = Math.ceil(dist / 0.25);
    if (!steps) return true;
    const r = PLAYER_RADIUS + 0.02;
    for (let i = 1; i <= steps; i++) {
      const x = x0 + ((x1 - x0) * i) / steps;
      const y = y0 + ((y1 - y0) * i) / steps;
      if (tileAt(map, x - r, y - r) !== T_FLOOR || tileAt(map, x + r, y - r) !== T_FLOOR || tileAt(map, x - r, y + r) !== T_FLOOR || tileAt(map, x + r, y + r) !== T_FLOOR) return false;
    }
    return true;
  }

  private updateVisibility(): void {
    const map = this.map;
    if (!map) return;
    const cx = Math.floor(this.px);
    const cy = Math.floor(this.py);
    const tile = cy * map.w + cx;
    if (tile === this.visTile) return;
    this.visTile = tile;
    const w = map.w;
    const h = map.h;
    const tiles = map.tiles;
    this.visible.fill(0);
    if (this.zone?.safe) {
      // Town is a lit, open place: nothing to hide.
      for (let i = 0; i < tiles.length; i++) this.reveal(i);
      return;
    }
    const R = 19;
    const x0 = Math.max(0, cx - R);
    const x1 = Math.min(w - 1, cx + R);
    const y0 = Math.max(0, cy - R);
    const y1 = Math.min(h - 1, cy + R);
    const ox = this.px;
    const oy = this.py;
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const dx = x + 0.5 - ox;
        const dy = y + 0.5 - oy;
        const dist = Math.hypot(dx, dy);
        if (dist > R) continue;
        const steps = Math.ceil(dist / 0.45);
        let seen = true;
        for (let i = 1; i < steps; i++) {
          const tx = Math.floor(ox + (dx * i) / steps);
          const ty = Math.floor(oy + (dy * i) / steps);
          // Only full walls stop sight. Thin props would smear ugly shadows across open rooms.
          if ((tx !== x || ty !== y) && tiles[ty * w + tx] === T_WALL) {
            seen = false;
            break;
          }
        }
        if (seen) this.reveal(y * w + x);
      }
    }
    // Walls bordering what you can see are revealed too, so that room edges read cleanly.
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const i = y * w + x;
        if (this.visible[i] !== 1 || tiles[i] === T_WALL) continue;
        for (let ny = Math.max(0, y - 1); ny <= Math.min(h - 1, y + 1); ny++) {
          for (let nx = Math.max(0, x - 1); nx <= Math.min(w - 1, x + 1); nx++) {
            const n = ny * w + nx;
            if (tiles[n] === T_WALL && !this.visible[n]) this.visible[n] = 2;
          }
        }
      }
    }
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const i = y * w + x;
        if (this.visible[i] === 2) this.reveal(i);
      }
    }
  }

  private reveal(i: number): void {
    this.visible[i] = 1;
    if (!this.explored[i]) {
      this.explored[i] = 1;
      this.exploredNew.push(i);
    }
  }

  isVisibleAt(x: number, y: number): boolean {
    const map = this.map;
    if (!map) return false;
    const tx = Math.floor(x);
    const ty = Math.floor(y);
    if (tx < 0 || ty < 0 || tx >= map.w || ty >= map.h) return false;
    return this.visible[ty * map.w + tx] === 1;
  }

  // ─── Actions ────────────────────────────────────────────────────────────

  rankOf(k: string): number {
    if (!this.hero || !this.derived) return 0;
    const base = this.hero.skills[k] ?? 0;
    return base > 0 ? base + Math.floor(this.derived.allSkills) : 0;
  }

  /** Whether a skill is castable right now, or the reason it isn't. */
  castBlock(k: string): 'cd' | 'mana' | 'lock' | 'none' | null {
    const def = SKILLS[k];
    if (!def || !this.rankOf(k)) return 'none';
    const cd = this.cooldowns.get(k);
    if (cd && cd.until > performance.now()) return 'cd';
    if (this.me[1] < rv(def.mana, this.rankOf(k))) return 'mana';
    if (performance.now() < this.nextCastAt) return 'lock';
    return null;
  }

  tryCast(k: string, x: number, y: number, id?: number): boolean {
    const def: SkillDef | undefined = SKILLS[k];
    if (!def || !this.zone || this.zone.safe) return false;
    if ((this.me[9] & (ME_DOWNED | ME_STUNNED)) !== 0) return false;
    const block = this.castBlock(k);
    if (block) return false;
    const speed = 1 + (this.derived?.atkSpd ?? 0) / 100;
    const now = performance.now();
    const cast = def.cast / speed;
    this.nextCastAt = now + ((cast + def.recover / speed) * 1000);
    this.castLockUntil = now + cast * 1000;
    this.send({ t: 'c', k, x, y, id });
    return true;
  }

  setMoveGoal(x: number, y: number): void {
    this.goal = { kind: 'point', x, y };
    this.path = null;
  }

  /** Throttled complaint for when the player tries something they can't do yet. */
  complain(text: string, color: string): void {
    const now = performance.now();
    if (now < this.complainUntil) return;
    this.complainUntil = now + 900;
    audio.play('deny');
    this.floats.push({ x: this.px, y: this.py - 1.4, text, color, t: 0, dur: 1, big: false, vx: 0 });
  }

  stopMoving(): void {
    this.goal = null;
    this.path = null;
  }

  get xpFrac(): number {
    if (!this.hero) return 0;
    return Math.min(1, this.me[5] / xpToNext(this.hero.level));
  }

  /** How a player unit should be drawn: its class dressed in what it wears. */
  look(unit: CUnit) {
    return dressedLook(unit.def as ClassId, unit.gear);
  }
}

function projColor(vis: string): string {
  switch (vis) {
    case 'firebolt':
    case 'fireball':
    case 'rocket':
      return '#f87800';
    case 'icebolt':
      return '#a4e4fc';
    case 'spark':
      return '#f8d878';
    case 'poison':
    case 'spit':
      return '#58d854';
    case 'arcane':
    case 'void':
      return '#b890fc';
    case 'holy':
    case 'hammer':
      return '#f8d878';
    case 'laser':
      return '#f83800';
    case 'plasma':
      return '#00e8d8';
    case 'shard':
      return '#d8b8f8';
    default:
      return '#bcbcbc';
  }
}

function bloodColor(def: string): string {
  const m = MONSTERS[def];
  if (!m) return '#d82800';
  if (m.noCorpse) return '#bcbcbc';
  const s = m.sprite;
  if (s === 'slime' || s === 'goblin' || s === 'xeno' || s === 'spitter') return '#58d854';
  if (s === 'skeleton' || s === 'archer' || s === 'mummy') return '#d8d8d8';
  if (s === 'golem') return '#b890fc';
  return '#a81000';
}

export { projColor };
